import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Search, X } from 'lucide-react';
import {
 formatAdminMoney,
 formatPaymentTypeLabel,
 getPaidAmount,
 getPlatformCommission,
 getVendorPayout,
} from '../../utils/adminPaymentTotals';
import { formatDate } from '../../utils/dateUtils';
import PrivateBookingBadge from '../shared/PrivateBookingBadge';
import { isPrivateBooking, PRIVATE_PAYOUT_EXCLUDED_NOTE } from '../../utils/bookingSource';

const isExpandableRow = (row) => row?.is_grouped && (row?.payment_count || 0) > 1;

const AdminGroupedPaymentsTable = ({
 data = [],
 columns = [],
 actions = [],
 loading = false,
 onSearch,
 searchPlaceholder = 'Search...',
 emptyMessage = 'No data available',
 getStatusConfig,
 pageSize = 10,
}) => {
 const [searchTerm, setSearchTerm] = useState('');
 const [expandedIds, setExpandedIds] = useState(() => new Set());
 const [currentPage, setCurrentPage] = useState(1);

 const filteredData = useMemo(() => {
 const list = Array.isArray(data) ? data : [];
 if (!searchTerm) return list;
 if (onSearch) return onSearch(list, searchTerm) || [];
 return list;
 }, [data, searchTerm, onSearch]);

 const paginatedData = useMemo(() => {
 const start = (currentPage - 1) * pageSize;
 return filteredData.slice(start, start + pageSize);
 }, [filteredData, currentPage, pageSize]);

 const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));

 const toggleExpanded = (rowId) => {
 setExpandedIds((prev) => {
 const next = new Set(prev);
 if (next.has(rowId)) next.delete(rowId);
 else next.add(rowId);
 return next;
 });
 };

 const renderCell = (row, column) =>
 column.render ? column.render(row) : row[column.key];

 const renderChildPaymentRow = (payment) => {
 const statusConfig = getStatusConfig?.(payment.status);
 const StatusIcon = statusConfig?.icon;
 const fee = getPlatformCommission(payment);
 const payout = getVendorPayout(payment);
 const isRefund = payment.status === 'refunded';
 const typeLabel = formatPaymentTypeLabel(payment.payment_type || payment.type);

 const isPrivate = isPrivateBooking(payment);

 return (
 <tr key={payment.id} className="bg-surface-muted/80">
 <td className="px-3 py-2 w-10" />
 <td className="px-4 py-2 pl-8">
 <p className="text-xs font-mono text-gray-600">
 {payment.payment_reference
 ? `#${String(payment.payment_reference).slice(0, 14)}`
 : 'Payment'}
 </p>
 <p className="text-xs text-gray-500">{typeLabel}</p>
 {isPrivate && (
 <div className="mt-1">
 <PrivateBookingBadge className="text-[10px]" />
 </div>
 )}
 </td>
 {columns.slice(1).map((column) => {
 if (column.key === 'amount') {
 return (
 <td key={column.key} className="px-4 py-2 text-right">
 <p className={`text-sm ${isRefund ? 'text-red-600' : 'text-gray-800'}`}>
 {isRefund ? '-' : ''}${formatAdminMoney(getPaidAmount(payment))}
 </p>
 </td>
 );
 }
 if (column.key === 'platform_fee') {
 return (
 <td key={column.key} className="px-4 py-2 text-right">
 {isPrivate && !isRefund ? (
 <p className="text-xs text-ink">{PRIVATE_PAYOUT_EXCLUDED_NOTE}</p>
 ) : (
 <p className="text-sm text-ink">
 {fee > 0 && !isRefund ? `-$${formatAdminMoney(fee)}` : '—'}
 </p>
 )}
 </td>
 );
 }
 if (column.key === 'vendor_payout') {
 return (
 <td key={column.key} className="px-4 py-2 text-right">
 {isPrivate && !isRefund ? (
 <p className="text-xs text-gray-500">{PRIVATE_PAYOUT_EXCLUDED_NOTE}</p>
 ) : (
 <p className="text-sm text-emerald-600">
 {!isRefund && payout > 0 ? `$${formatAdminMoney(payout)}` : '—'}
 </p>
 )}
 </td>
 );
 }
 if (column.key === 'created_at') {
 return (
 <td key={column.key} className="px-4 py-2">
 <span className="text-xs text-gray-600">
 {formatDate(payment.created_at, 'medium')}
 </span>
 </td>
 );
 }
 if (column.key === 'status' && statusConfig) {
 return (
 <td key={column.key} className="px-4 py-2">
 <span
 className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}
 >
 {StatusIcon && <StatusIcon className="w-3 h-3 mr-1" />}
 {statusConfig.label}
 </span>
 </td>
 );
 }
 return (
 <td key={column.key} className="px-4 py-2">
 {renderCell(payment, column)}
 </td>
 );
 })}
 {actions.length > 0 && <td className="px-4 py-2" />}
 </tr>
 );
 };

 if (loading) {
 return (
 <div className="p-8 text-center">
 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ink mx-auto" />
 <p className="mt-2 text-gray-600 text-sm">Loading...</p>
 </div>
 );
 }

 const firstColumn = columns[0];

 return (
 <div className="bg-surface rounded-lg border border-gray-200 overflow-hidden">
 <div className="p-4 border-b border-gray-200">
 <div className="relative max-w-md">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
 <input
 type="text"
 value={searchTerm}
 onChange={(e) => {
 setSearchTerm(e.target.value);
 setCurrentPage(1);
 }}
 placeholder={searchPlaceholder}
 className="w-full pl-10 pr-9 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ink"
 />
 {searchTerm && (
 <button
 type="button"
 onClick={() => setSearchTerm('')}
 className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
 >
 <X className="h-4 w-4" />
 </button>
 )}
 </div>
 </div>

 <div className="overflow-x-auto">
 <table className="min-w-full divide-y divide-gray-200">
 <thead className="bg-surface-muted">
 <tr>
 <th className="w-10 px-3 py-3" aria-label="Expand" />
 {columns.map((column) => (
 <th
 key={column.key}
 className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${column.width || ''}`}
 >
 {column.label}
 </th>
 ))}
 {actions.length > 0 && (
 <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-36">
 Actions
 </th>
 )}
 </tr>
 </thead>
 <tbody className="divide-y divide-gray-200">
 {paginatedData.length === 0 ? (
 <tr>
 <td
 colSpan={columns.length + 1 + (actions.length > 0 ? 1 : 0)}
 className="px-6 py-12 text-center text-gray-500 text-sm"
 >
 {emptyMessage}
 </td>
 </tr>
 ) : (
 paginatedData.map((row) => {
 const expandable = isExpandableRow(row);
 const isOpen = expandedIds.has(row.id);

 return (
 <React.Fragment key={row.id}>
 <tr
 className={`hover:bg-surface-muted ${expandable ? 'cursor-pointer' : ''} ${isOpen ? 'bg-surface-muted' : ''}`}
 onClick={() => expandable && toggleExpanded(row.id)}
 >
 <td className="px-3 py-3 align-top">
 {expandable ? (
 <button
 type="button"
 className="p-0.5 text-gray-500 hover:text-gray-800"
 aria-expanded={isOpen}
 aria-label={isOpen ? 'Collapse payments' : 'Expand payments'}
 onClick={(e) => {
 e.stopPropagation();
 toggleExpanded(row.id);
 }}
 >
 {isOpen ? (
 <ChevronDown className="w-4 h-4" />
 ) : (
 <ChevronRight className="w-4 h-4" />
 )}
 </button>
 ) : (
 <span className="inline-block w-4" />
 )}
 </td>
 <td className="px-4 py-3 align-top" onClick={(e) => e.stopPropagation()}>
 {firstColumn?.render ? firstColumn.render(row) : null}
 {expandable && (
 <p className="text-xs text-gray-400 mt-0.5">
 {row.payment_count} payments — click to {isOpen ? 'hide' : 'view'}
 </p>
 )}
 </td>
 {columns.slice(1).map((column) => (
 <td key={column.key} className="px-4 py-3 align-top">
 {renderCell(row, column)}
 </td>
 ))}
 {actions.length > 0 && (
 <td
 className="px-4 py-3 text-right align-top"
 onClick={(e) => e.stopPropagation()}
 >
 <div className="flex flex-wrap justify-end gap-1">
 {actions.map((action, idx) => (
 <button
 key={idx}
 type="button"
 onClick={() => action.onClick(row)}
 className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-surface-muted hover:bg-gray-100 rounded-md"
 >
 {action.icon && <action.icon className="w-3 h-3 mr-1" />}
 {action.label}
 </button>
 ))}
 </div>
 </td>
 )}
 </tr>
 {expandable && isOpen &&
 (row.payment_items || []).map((payment) =>
 renderChildPaymentRow(payment)
 )}
 </React.Fragment>
 );
 })
 )}
 </tbody>
 </table>
 </div>

 {filteredData.length > pageSize && (
 <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
 <span>
 Page {currentPage} of {totalPages} ({filteredData.length} orders)
 </span>
 <div className="flex gap-2">
 <button
 type="button"
 disabled={currentPage <= 1}
 onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
 className="px-3 py-1 border rounded-md disabled:opacity-40 hover:bg-surface-muted"
 >
 Previous
 </button>
 <button
 type="button"
 disabled={currentPage >= totalPages}
 onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
 className="px-3 py-1 border rounded-md disabled:opacity-40 hover:bg-surface-muted"
 >
 Next
 </button>
 </div>
 </div>
 )}
 </div>
 );
};

export default AdminGroupedPaymentsTable;
